import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { ThemeContext, type ThemeContextValue, type ThemePreference } from './theme-context';

const STORAGE_KEY = 'stock.theme';

function readInitialTheme(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch {
    // 忽略：讀不到偏好就走系統。
  }
  return 'system';
}

/**
 * §10.6：完整的淺色調色盤在裸 :root，深色在
 * `@media (prefers-color-scheme: dark) :root:not([data-theme="light"])` 與
 * `:root[data-theme="dark"]` 各定義一次，所以手動切換**在兩個方向都會贏**。
 * 這個 provider 只負責蓋／拿掉 data-theme，顏色的事全部在 tokens.css。
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePreference>(readInitialTheme);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') {
      delete root.dataset['theme'];
    } else {
      root.dataset['theme'] = theme;
    }
    try {
      if (theme === 'system') {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, theme);
      }
    } catch {
      // 記不住偏好不是致命錯誤。
    }
  }, [theme]);

  const setTheme = useCallback((next: ThemePreference) => {
    setThemeState(next);
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({ theme, setTheme }), [theme, setTheme]);

  return <ThemeContext value={value}>{children}</ThemeContext>;
}
